/**
 * La disponibilité d'un bénévole : des CRÉNEAUX (2026-09-26).
 *
 * Miroir de cloud-api `core/disponibilite.py` — la même règle, pas une
 * réécriture : un créneau couvre `[start, end)`, une journée entière vaut
 * `00:00 → 24:00`, et `null` veut dire « on ne sait pas », ce qui n'est PAS
 * « absent ».
 */
export interface Slot {
  /** Jour ISO `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`, inclus. */
  start: string;
  /** `HH:MM`, exclu. `24:00` = fin de journée. */
  end: string;
}

export const DEBUT_JOURNEE = '00:00';
export const FIN_JOURNEE = '24:00';

export const journeeEntiere = (date: string): Slot =>
  ({ date, start: DEBUT_JOURNEE, end: FIN_JOURNEE });

export const estJourneeEntiere = (s: Slot): boolean =>
  s.start === DEBUT_JOURNEE && s.end === FIN_JOURNEE;

/** `HH:MM` → minutes depuis minuit ; `null` si illisible. */
export const minutes = (hhmm?: string | null): number | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/** Le bénévole est-il là ce jour-là ? (peu importe l'heure) */
export const presentLeJour = (slots: Slot[] | null | undefined, jour: string): boolean =>
  !slots || !slots.length || slots.some((s) => s.date === jour);

/**
 * Le bénévole est-il là à cette heure-là ? Sans heure connue, le jour suffit.
 * Une disponibilité non renseignée compte comme « oui » : on ne signale rien
 * tant qu'on ne sait pas.
 */
export const couvre = (
  slots: Slot[] | null | undefined, jour: string, heure?: string | null,
): boolean => {
  if (!slots || !slots.length) return true;
  const t = minutes(heure ? heure.slice(0, 5) : null);
  return slots.some((s) => {
    if (s.date !== jour) return false;
    if (t === null) return true;
    const a = minutes(s.start), b = minutes(s.end);
    return a !== null && b !== null && a <= t && t < b;
  });
};

/** Les jours couverts, triés, sans doublon. */
export const joursDe = (slots: Slot[] | null | undefined): string[] =>
  Array.from(new Set((slots || []).map((s) => s.date))).sort();

/** « sam. 3 oct. » ou « sam. 3 oct. 08:00–13:00 ». */
export const libelleCreneau = (s: Slot, lang: string): string => {
  const jour = new Date(`${s.date}T12:00:00`).toLocaleDateString(lang, {
    weekday: 'short', day: 'numeric', month: 'short',
  });
  return estJourneeEntiere(s) ? jour : `${jour} ${s.start}–${s.end}`;
};

/**
 * Toute une disponibilité en une ligne : le jour une seule fois, les heures
 * qui se suivent fusionnées. « sam. 3 oct. 08:00–09:00, 10:00–17:00 ·
 * dim. 4 oct. » — et non dix-huit créneaux d'une heure qui répètent le jour.
 * Fusionner ne change rien au sens : `[08:00, 09:00)` puis `[09:00, 10:00)`
 * couvrent exactement `[08:00, 10:00)`.
 */
export const libelleDisponibilite = (
  slots: Slot[] | null | undefined, lang: string, sep = ' · ',
): string => {
  const parJour = new Map<string, Array<[number, number]>>();
  (slots || []).forEach((s) => {
    const a = minutes(s.start), b = minutes(s.end);
    if (a === null || b === null) return;
    parJour.set(s.date, [...(parJour.get(s.date) || []), [a, b]]);
  });
  const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return Array.from(parJour.keys()).sort().map((date) => {
    const fusion: Array<[number, number]> = [];
    parJour.get(date)!.sort((x, y) => x[0] - y[0]).forEach(([a, b]) => {
      const der = fusion[fusion.length - 1];
      if (der && a <= der[1]) der[1] = Math.max(der[1], b);
      else fusion.push([a, b]);
    });
    const jour = libelleCreneau(journeeEntiere(date), lang);
    if (fusion.length === 1 && fusion[0][0] === 0 && fusion[0][1] === 24 * 60) return jour;
    return `${jour} ${fusion.map(([a, b]) => `${hhmm(a)}–${hhmm(b)}`).join(', ')}`;
  }).join(sep);
};
